import axios from "axios";
import { api } from "..";
import { CrmItemViewModel, FilterType, FilterViewModel, PipelineReportDisplayBy, DateFilterMode, PipelineReportColumn } from '../../types/domain';

//convert enum numeric values to their key names
const getEnumKeyFromValue = <T>(enumObj: T, value: number): string => {
  return (enumObj as any)[value] || '';
};

//map and group pipeline stages by pipeline
const mapPipelineStages = (stages: any[]) => {
  const grouped = new Map<string, { pipelineId: string; pipelineStages: any[] }>();

  for (const stage of stages) {
    const pipelineId = stage.group?.name ?? '';

    if (!grouped.has(pipelineId)) {
      grouped.set(pipelineId, { pipelineId, pipelineStages: [] });
    }

    grouped.get(pipelineId)!.pipelineStages.push(mapSelectOption(stage));
  }

  return Array.from(grouped.values());
};

const mapSelectOption = (item: any) => ({
  value: String(item.value ?? ''),
  label: item.text ?? '',
  disabled: !!item.disabled
});

const mapAvailableFilters = (filters: any[]): FilterViewModel[] => {
  return filters.map((f: any) => ({
    id: f.id?.toString() ?? '',
    name: f.name ?? '',
    filterType: typeof f.filterType === 'number' ? f.filterType : (Number(f.filterType) ?? FilterType.TextBox),
    values: (f.values || []).map(mapSelectOption),
    autocomplete: !!f.autocomplete,
    autocompleteUrl: f.autocompleteUrl,
  } as FilterViewModel));
};

const mapResponseToViewModel = (response: any): CrmItemViewModel => {
  return {
    pipelineId: response.data.pipelineId,
    pipelineStages: (response.data.pipelineStages || []).map((s: any) => String(s)),
    libraryItemType: response.data.libraryItemType ?? undefined,
    displayBy: typeof response.data.displayBy !== 'undefined' ? getEnumKeyFromValue(PipelineReportDisplayBy, response.data.displayBy) : PipelineReportDisplayBy[PipelineReportDisplayBy.Company],
    displayStyle: response.data.displayStyle ?? undefined,
    group: response.data.group ?? undefined,
    currentUserOnly: !!response.data.currentUserOnly,
    name: response.data.name ?? undefined,
    noResult: typeof response.data.noResult !== 'undefined' ? Number(response.data.noResult) : 100,
    dateFilterMode: typeof response.data.dateFilterMode !== 'undefined' ? getEnumKeyFromValue(DateFilterMode, response.data.dateFilterMode) : DateFilterMode[DateFilterMode.Fixed],
    sortColumn: typeof response.data.sortColumn !== 'undefined' ? getEnumKeyFromValue(PipelineReportColumn, response.data.sortColumn) : PipelineReportColumn[PipelineReportColumn.Company],
    sortDesc: !!response.data.sortDesc,
    visibleColumns: (response.data.visibleColumns || []).map((s: any) => getEnumKeyFromValue(PipelineReportColumn, s)),
    dynamicFilters: (response.data.dynamicFilters || []).map((df: any) => ({ filterId: df.filterId ?? df.FilterId ?? df.id ?? '', values: (df.values || df.Values || []).map((v: any) => String(v)) })),
    theme: response.data.theme,

    availablePipelines: (response.data.availablePipelines || []).map(mapSelectOption),
    availableCrmTypes: (response.data.availableCrmTypes || []).map(mapSelectOption),
    availableDisplayBy: (response.data.availableDisplayBy || []).map(mapSelectOption),
    availableGroups: (response.data.availableGroups || []).map(mapSelectOption),
    availableDateFilterModes: (response.data.availableDateFilterModes || []).map(mapSelectOption),
    availableColumns: (response.data.availableColumns || []).map(mapSelectOption),
    availableFilters: mapAvailableFilters(response.data.availableFilters || []),
    availablePipelineStages: mapPipelineStages(response.data.availablePipelineStages ?? []),
  };
};

export const getLibraryItemCreateViewModel = async (pipelineId?: string): Promise<CrmItemViewModel> => {
  const url = pipelineId ? `/api/libraryitem/create?pipelineId=${encodeURIComponent(pipelineId)}` : `/api/libraryitem/create`;
  const response = await axios.get(url);
  return mapResponseToViewModel(response);
}

export const getLibraryItemEditViewModel = async (libraryItemId: string): Promise<CrmItemViewModel> => {
  const url = `/api/libraryitem/edit/${encodeURIComponent(libraryItemId)}`;
  const response = await axios.get(url);
  return mapResponseToViewModel(response);
}

export const createLibraryItem = async (viewModel: CrmItemViewModel): Promise<string> => {
  const response = await api.post('/libraryitem/create', viewModel);
  if (response.status === axios.HttpStatusCode.Created) {
    const responseDto = response.data;
    return responseDto.data;
  }
  throw new Error(`Failed to create library item. Status: ${response.status}`);
}

export const updateLibraryItem = async (libraryItemId: string, viewModel: CrmItemViewModel): Promise<void> => {
  const response = await api.post(`/libraryitem/edit/${encodeURIComponent(libraryItemId)}`, viewModel);
  if (response.status !== axios.HttpStatusCode.Ok && response.status !== axios.HttpStatusCode.NoContent) {
    throw new Error(`Failed to update library item. Status: ${response.status}`);
  }
}

export const getReport = async (viewModel: CrmItemViewModel): Promise<any> => {
  const response = await api.post('/libraryitem/getreport', viewModel);
  if (response.status === axios.HttpStatusCode.Ok) {
    return response.data;
  }
  throw new Error(`Failed to get report. Status: ${response.status}`);
}


